import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { BloodworkAnalysis, PanelMarker, FurtherConsiderations, Section } from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { Footer } from '../../Footer';
import { TabsComponent } from '../tabs-component';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { ThyroidRegulationInsights } from './thyroid-regulation-insights';
import { NutrientInsights } from './nutrient-insights';
import { StressAdrenalRegulationInsights } from './stress-adrenal-regulation-insights';
import { DehydrationInsights, DEHYDRATION_PANEL_ENABLED } from './dehydration-insights';
import {
  InsightsReviewNotice,
  insightsPendingClinicalReview,
} from './insights-review-notice';
import { visibleExceptProd } from '../../utils/featureVisibility';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SectionVideoIcon } from '../common/section-video-icon';
import { sectionTextMarkers } from '../../helpers/customTextMarkers';
import {
  insightsSourceAnalysis,
  previousCollectionLabel,
} from '../../helpers/previousOnlyMarkers';
import './previous-collection-insights.css';

type Props = {
  section: Section;
  /**
   * Unique key for this panel's DOM ids, when `section` alone is not one.
   *
   * Every practitioner-added section on a test renders as Section.CUSTOM_MARKERS
   * — that is what gives them the plain reference-range treatment — so without
   * this their "Include in PDF" checkboxes would all share one id and clicking
   * a label would toggle the first section's checkbox rather than its own.
   */
  sectionKey?: string;
  /** The practitioner's name for this section, when it is one of theirs. */
  practitionerSectionName?: string;
  panelTitle: string;
  bloodworkAnalysis: BloodworkAnalysis;
  panelMarkers: PanelMarker[];
  furtherConsiderations: FurtherConsiderations;
  evalSmall: number;
  refSmall: number;
  evalLarge: number;
  refLarge: number;
  // Optional: sections with no bibliography anchor (e.g. the practitioner's own
  // custom markers) omit it, and the "learn more" affordance is skipped.
  infoLink?: string;
  bloodworkEntryStore: BloodworkEntryStore;
  enabledSupplementBrands: string[];
  collapsed: boolean;
  onToggle: () => void;
  includeInPdf: boolean;
  onIncludeInPdfToggle: () => void;
  // Optional extra checkboxes rendered in the header to the LEFT of the
  // "Include in PDF" checkbox (e.g. the Immune Activity section's quick
  // "Only Show Section Signals" / "Show Absolutes" view toggles). Listed
  // left-to-right in array order.
  extraToggles?: Array<{
    id: string;
    label: string;
    checked: boolean;
    onToggle: () => void;
    title?: string;
  }>;
};

// LAYOUT: the insights panel and the tabs must NEVER overlap. When there isn't
// room for both, they stack instead.
//
// Both halves of this row are fixed-width panels, so a percentage split can't
// hold them:
//   - the insights panel is a fixed 300px (.evaluationPanel), needing 330px
//     with the column's padding;
//   - the Reference Range tab's bar-graph panel is a fixed 760px
//     (.ref-range-analysis-panel), needing 790px.
// A percentage column narrower than its fixed-width child does not shrink the
// child — the child spills out of the column and lands on whatever is beside
// it. With the old 3/9 split the insights column only reached 330px once the
// window passed ~1360px, so below that the 300px panel hung over into the tabs
// column and the tab strip and Results panel drew on top of it.
//
// So the split is by CONTENT, not percentage:
//   - insights column is `xl="auto"` — exactly as wide as its 300px panel, at
//     every window size, so it can never overflow;
//   - tabs column is `xl` (flex-grow) — it takes all remaining width, which
//     also puts the leftover space into the tabs rather than stranding it.
// Below `xl` (1200px) both are xs={12} and simply stack: insights panel on
// top, tabs and Results panel below, each full width. 1200px is the point
// where 330 + 790 = 1120px of fixed panels still fits side by side.
//
// The evalLarge / refLarge props (the old 3/9 spans) are consequently unused
// here — kept on Props because every caller still passes them.
export class BloodworkTestAnalysisSection extends React.Component<Props, {}> {

  /**
   * This section's insights panel, built from `ba`.
   *
   * `ba` is normally the current analysis. For a section made entirely of
   * previous-only markers it is the PREVIOUS one, and evaluation() labels what
   * comes back with that collection's date.
   */
  panelFor = (ba: BloodworkAnalysis) => {
    const { section } = this.props;
    if (section === Section.RBC) {
      return <AnemiaStatus bloodworkAnalysis={ba} />;
    } else if (section === Section.WBC && ba.wbc.hasValue()) {
      return <WBCEvaluation bloodworkAnalysis={ba} />;
    } else if (section === Section.GLUCOSE) {
      // When the insights panel renders nothing (e.g. amylase is the only
      // glucose-section marker), skip the eval Col entirely so the Results
      // panel left-aligns instead of sitting behind an empty left gap.
      if (!GlucoseRegulationInsights.shouldRender(ba)) {
        return null;
      }
      return <GlucoseRegulationInsights bloodworkAnalysis={ba} />;
    } else if (
      section === Section.ELECTROLYTES &&
      // Hidden in every shipped build — see DEHYDRATION_PANEL_ENABLED. It is a
      // single exported flag rather than the condition repeated here because
      // AnemiaStatus reads the SAME flag to decide whether to keep rendering
      // the dehydration line: one constant means the panel and the RBC line
      // can never both show, or both vanish.
      DEHYDRATION_PANEL_ENABLED
    ) {
      // Hydration gauge. Needs at least one of RBC/HGB/HCT to say anything, so
      // skip the eval Col entirely when there's no hemoconcentration panel —
      // same left-align behaviour as the Glucose and Adrenal sections.
      if (!DehydrationInsights.shouldRender(ba)) {
        return null;
      }
      return <DehydrationInsights bloodworkAnalysis={ba} />;
    } else if (section === Section.THYROID && visibleExceptProd()) {
      // ASK THE PANEL, like Glucose, Hydration, Vitamins & Minerals and Stress
      // & Adrenal. The env gate says whether this build shows the panel at all;
      // it says nothing about whether THIS test gives it anything to draw, and
      // only the second question decides whether the eval column earns its
      // width.
      //
      // evaluation() keeps the column whenever panelFor returns an element, and
      // an element is truthy however little it renders. So a section built on a
      // marker the panel does not read wrapped a column around a component that
      // draws nothing: 300px of empty beside the bars, and a full-width blank
      // band below 1200px, where evalSmall is 12.
      //
      // Free T4 Index is that marker, and the only one. It is entered rather
      // than derived (Bloodwork.freeT4Index), getThyroidPanelMarkers charts it,
      // and it is the one of those eleven the panel's MARKER_ROW leaves out. A
      // custom marker filed into Thyroid cannot reach this on its own: placement
      // needs the section to have a shipped marker already (withCustomMarkers).
      //
      // shouldRender is exactly render()'s own guard (both come down to
      // hasAnyThyroidMarker, via exportInsights), so this cannot hide a panel
      // that would have drawn something. It also matches what printsInsightsPanel
      // already asks in the PDF, so the two agree the day Thyroid leaves
      // SECTIONS_PENDING_CLINICAL_REVIEW and starts printing.
      if (!ThyroidRegulationInsights.shouldRender(ba)) {
        return null;
      }
      return <ThyroidRegulationInsights bloodworkAnalysis={ba} />;
    } else if (section === Section.NUTRIENTS) {
      // Visible on slava for review (2026-08-21) but still held from prod
      // until the one-carbon content has had a clinical review. Unlike those two this panel has NO gauge — the marker
      // is the finding here, not a proxy for one (see nutrient-insights.tsx).
      if (!NutrientInsights.shouldRender(ba)) {
        return null;
      }
      return <NutrientInsights bloodworkAnalysis={ba} />;
    } else if (section === Section.STRESS_ADRENAL) {
      // Skip the eval Col entirely when the panel has nothing to say, so the
      // Results panel left-aligns like sections without an insights panel,
      // instead of sitting in the right column behind an empty left gap.
      //
      // ASK THE PANEL, exactly like the three sections above. This gate used to
      // call adrenalGauge(ba.sodiumPotassiumRatio) with no escalators, which
      // dated from when the gauge required Na/K. Na/K is optional now, so that
      // call answered "nothing to show" for any client whose panel carried
      // cortisol or DHEA-S but no electrolytes — while the PDF, which asks
      // shouldRender, printed a full gauge for the same test. The client's
      // document showed a finding the practitioner never saw on screen.
      if (!StressAdrenalRegulationInsights.shouldRender(ba)) {
        return null;
      }
      return <StressAdrenalRegulationInsights bloodworkAnalysis={ba} />;
    } else {
      return null;
    }
  }

  evaluation = () => {
    const { bloodworkAnalysis, evalSmall, panelMarkers, section } = this.props;
    // A section built entirely from previous-only markers (values last test,
    // none this one) has nothing CURRENT for these panels to read: every one
    // of them interprets the test it is given. Rather than a blank gauge, it
    // gets the panel for the collection those bars came from, captioned with
    // that date. A panel is never built from a mix of the two tests, and with
    // no previous analysis at all (a single test) there is no panel.
    const insightsAnalysis = insightsSourceAnalysis(
      section,
      panelMarkers,
      bloodworkAnalysis,
    );
    if (!insightsAnalysis) {
      return null;
    }
    const panel = this.panelFor(insightsAnalysis);
    if (!panel) {
      return null;
    }
    const fromPreviousCollection = insightsAnalysis !== bloodworkAnalysis;
    return (
      <Col xs={evalSmall} xl="auto">
        {/*
          Above everything, including the previous-collection caption: it is a
          note about the panel's release state, not about the data in it, and
          the practitioner should read it before the gauge.
        */}
        {insightsPendingClinicalReview(section) && <InsightsReviewNotice />}
        {fromPreviousCollection ? (
          <div className="previous-collection-insights">
            <div className="previous-collection-caption">
              {previousCollectionLabel(insightsAnalysis.bloodwork)}
            </div>
            {panel}
          </div>
        ) : (
          panel
        )}
      </Col>
    );
  }

  // `fullWidth` when this section has no left-hand insights/evaluation panel —
  // the tabs then take the whole card rather than leaving the missing column's
  // width as dead space on the right, which squeezed wide tab content (Notes,
  // its digest cards, Root Causes) for no reason.
  analysis = (fullWidth: boolean) => {
    const { section, bloodworkAnalysis, panelMarkers, furtherConsiderations,
      refSmall, bloodworkEntryStore, infoLink, enabledSupplementBrands } = this.props;
      return (
        <Col xs={fullWidth ? 12 : refSmall} xl={fullWidth ? 12 : true}>
          <TabsComponent
            section={section}
            panelMarkers={panelMarkers}
            bloodworkAnalysis={bloodworkAnalysis} 
            furtherConsiderations={furtherConsiderations}
            bloodworkEntryStore={bloodworkEntryStore}
            infoLink={infoLink}
            enabledSupplementBrands={enabledSupplementBrands}
            practitionerSectionName={this.props.practitionerSectionName}
            noteSection={this.props.sectionKey}
          />
        </Col>
      );
  }

  render() {
    const { panelTitle, panelMarkers, collapsed, onToggle, section, sectionKey, includeInPdf, onIncludeInPdfToggle, extraToggles } = this.props;
    // A section can be made ENTIRELY of practitioner-typed text results — a
    // urinalysis panel is exactly that — so "no bar graphs" is no longer the
    // same question as "nothing to show". Asked here rather than passed in for
    // the same reason tabs-component asks it: this card already knows which
    // section it is, and every one of the eighteen call sites would otherwise
    // have to be given a second marker list to keep in step with the first.
    const hasTextMarkers =
      sectionTextMarkers(
        this.props.bloodworkAnalysis,
        section,
        this.props.practitionerSectionName,
      ).length > 0;
    const domKey = sectionKey || section;
    const chevron = collapsed ? 'chevron-down' : 'chevron-up';
    // Built once so the tabs column knows whether the left column is there.
    const evaluationCol = this.evaluation();
    return (
      <>
      { panelMarkers.length > 0 || hasTextMarkers ?
        <Card>
          <Card.Header
            onClick={onToggle}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <FontAwesomeIcon icon={chevron} />&nbsp;&nbsp;{panelTitle}
              <SectionVideoIcon section={section} />
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
              {(extraToggles ?? []).map(toggle => (
                <label
                  key={toggle.id}
                  htmlFor={toggle.id}
                  onClick={e => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                  title={toggle.title}
                >
                  <span style={{ marginRight: '6px' }}>{toggle.label}</span>
                  <input
                    id={toggle.id}
                    type="checkbox"
                    className="lsm-styled-checkbox"
                    checked={toggle.checked}
                    onChange={toggle.onToggle}
                  />
                </label>
              ))}
              <label
                htmlFor={`include-in-pdf-${domKey}`}
                onClick={e => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                title="Include this section in the Analysis PDF"
              >
                <span style={{ marginRight: '6px' }}>Include in PDF</span>
                <input
                  id={`include-in-pdf-${domKey}`}
                  type="checkbox"
                  className="lsm-styled-checkbox"
                  checked={includeInPdf}
                  onChange={onIncludeInPdfToggle}
                />
              </label>
            </span>
          </Card.Header>
          {!collapsed && (
            <>
              <Card.Body>
                <Row>
                  {evaluationCol}
                  {this.analysis(!evaluationCol)}
                </Row>
              </Card.Body>
              <Card.Footer>
                <Footer />
              </Card.Footer>
            </>
          )}
        </Card>
        : null
      }
      </>
    );
  }
}
